import React, {useState} from "react";
import {Button, Col, Input, Modal, Row, Select, Space, Table, Tag} from "antd";
import {PlusOutlined, EditOutlined, DeleteOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../hooks/useStore";
import {AdjustmentTypeModal} from "./AdjustmentTypeModal";
import {useFetch} from "hooks/useFetch";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";

const {Option} = Select;

const adjustmentTypeCategories = {
    financial: "Finansal",
    financialWithoutInvoice: "Faturasız Finansal",
    other: "Diğer"
};

export const AdjustmentTypesPage = () => {
    const {t} = useTranslation(["common"]);
    const stores = useStore();

    const [filters, setFilters] = useState({name: "", type: "", category: "", skip: 0, limit: 20});
    const [modalVisible, setModalVisible] = useState(false);
    const [editData, setEditData] = useState(null);

    const [
        loadingCourierAdjustmentTypes,
        courierAdjustmentTypes,
        _,
        __,
        refetchCourierAdjustmentTypes
    ] = useFetch({
        fetchFn: stores.courierAdjustmentStore.getAdjustmentTypes.bind(null, {
            ...filters
        }),
        dependencies: [JSON.stringify(filters)]
    });

    // Yeni ekle
    const handleAdd = () => {
        setEditData(null);
        setModalVisible(true);
    };

    // Düzenle
    const handleEdit = record => {
        setEditData(record);
        setModalVisible(true);
    };

    // Sil
    const handleDelete = record => {
        Modal.confirm({
            title: "Eklenti/Kesinti Tipi Sil",
            content: `"${record.name}" tipini silmek istediğinize emin misiniz?`,
            okText: "Evet, Sil",
            okType: "danger",
            cancelText: "İptal",
            onOk: async () => {
                const result = await stores.courierAdjustmentStore.deleteAdjustmentType(record._id);
                if (result && result.success !== false) {
                    setFilters({...filters, skip: 0});
                }
            }
        });
    };

    // Modal başarılı sonuç
    const handleModalSuccess = async () => {
        setModalVisible(false);
        setEditData(null);
        setFilters({...filters, skip: 0});
        await refetchCourierAdjustmentTypes();
    };

    // Tablo kolonları
    const columns = [
        {
            title: "İsim",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name, "tr"),
            onCell: record => ({
                "data-label": "İsim"
            })
        },
        {
            title: "Kategori",
            dataIndex: "category",
            key: "category",
            render: category => <Tag>{adjustmentTypeCategories[category] || "Diğer"}</Tag>,
            onCell: record => ({
                "data-label": "Kategori"
            })
        },
        {
            title: "Tip",
            dataIndex: "type",
            key: "type",
            render: type => (
                <Tag color={type === "addition" ? "green" : "red"}>
                    {type === "addition" ? "Eklenti" : "Kesinti"}
                </Tag>
            ),
            filters: [
                {text: "Eklenti", value: "addition"},
                {text: "Kesinti", value: "deduction"}
            ],
            onFilter: (value, record) => record.type === value,
            onCell: record => ({
                "data-label": "Tip"
            })
        },
        {
            title: "Yetkili Kullanıcılar",
            dataIndex: "authorizedUsers",
            key: "authorizedUsers",
            render: users => {
                if (!users || users.length === 0) return "-";
                if (users.length === 1) return users[0].name;
                return `${users.length} kullanıcı`;
            },
            onCell: record => ({
                "data-label": "Yetkili Kullanıcılar"
            })
        },
        {
            title: "Oluşturma Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            render: date => {
                if (!date) return "-";
                return new Date(date).toLocaleDateString("tr-TR", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit"
                });
            },
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            onCell: record => ({
                "data-label": "Oluşturma Tarihi"
            })
        },
        {
            title: "Aksiyon",
            key: "action",
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    >
                        Düzenle
                    </Button>
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                        size="small"
                    >
                        Sil
                    </Button>
                </Space>
            ),
            onCell: record => ({
                "data-label": "Aksiyon"
            })
        }
    ];

    return (
        <div style={{padding: "24px"}}>
            <h2>Eklenti/Kesinti Tipleri</h2>

            {/* Filtreler */}
            <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                <Col xs={24} sm={12} md={6} lg={6}>
                    <Input
                        placeholder="İsim ile filtrele"
                        value={filters.name}
                        onChange={e => setFilters({...filters, name: e.target.value, skip: 0})}
                        allowClear
                    />
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                    <Select
                        placeholder="Tip seçin"
                        value={filters.type || undefined}
                        onChange={value => setFilters({...filters, type: value || "", skip: 0})}
                        style={{width: "100%"}}
                        allowClear
                    >
                        <Option value="addition">Eklenti</Option>
                        <Option value="deduction">Kesinti</Option>
                    </Select>
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                    <Select
                        placeholder="Kategori seçin"
                        value={filters.category || undefined}
                        onChange={value => setFilters({...filters, category: value || "", skip: 0})}
                        style={{width: "100%"}}
                        allowClear
                    >
                        <Option value="financial">Finansal</Option>
                        <Option value="financialWithoutInvoice">Faturasız Finansal</Option>
                        <Option value="other">Diğer</Option>
                    </Select>
                </Col>
                <Col xs={24} sm={24} md={6} lg={6} style={{textAlign: "right"}}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Yeni
                    </Button>
                </Col>
            </Row>

            {/* Tablo */}
            <ResponsiveDataTable
                columns={columns}
                dataSource={courierAdjustmentTypes?.data?.rows || []}
                rowKey="_id"
                loading={loadingCourierAdjustmentTypes}
                pagination={{
                    current: filters.limit > 0 ? Math.floor(filters.skip / filters.limit) + 1 : 1,
                    pageSize: filters.limit,
                    total: courierAdjustmentTypes?.data?.dataCount || 0,
                    showTotal: total => `Toplam ${total} kayıt`,
                    onChange: (page, pageSize) => {
                        setFilters({...filters, skip: (page - 1) * pageSize, limit: pageSize});
                    }
                }}
            />

            {/* Modal */}
            <AdjustmentTypeModal
                visible={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditData(null);
                }}
                onSuccess={handleModalSuccess}
                editData={editData}
            />
        </div>
    );
};
